import { useMemo, useState } from 'react'

import { scaleBand, scaleLinear } from '@visx/scale'
import { Group } from '@visx/group'
import { Line, Polygon } from '@visx/shape'
import { LinearGradient } from '@visx/gradient'
import { ParentSize } from '@visx/responsive'
import { FunnelChartData } from 'src/components/analytics/types/funnel'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'

const TOP_MARGIN = 10
const BOTTOM_MARGIN = 10

function Funnel({ width, height, data }: { width: number; height: number; data: FunnelChartData[] }) {
  const [hoveredBar, setHoveredBar] = useState<number | null>(null)
  const xMax = width
  const yMax = height - (TOP_MARGIN + BOTTOM_MARGIN)

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        range: [0, xMax],
        round: true,
        domain: data.map(d => d.name),
        padding: 0.15
      }),
    [xMax, data]
  )
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [0, yMax / 2],
        round: false,
        domain: [0, Math.max(...data.map(d => d.value))]
      }),
    [yMax, data]
  )

  return width < 10 ? null : (
    <div className='relative'>
      <svg
        width={width}
        height={height}
        onMouseLeave={() => setHoveredBar(null)}
        className={hoveredBar ? 'cursor-pointer' : ''}
      >
        <LinearGradient id='hovered-funnel-bar-gradient' from='#01CCA7' fromOpacity={0.2} to='#fff' toOpacity={0.04} />
        <Group top={TOP_MARGIN}>
          {data.map((d, i) => {
            const barX = xScale(d.name) ?? 0
            return (
              <Line
                key={`line-${i}`}
                from={{ x: barX, y: 0 }}
                to={{ x: barX, y: yMax }}
                stroke='#DBDBDB'
                strokeWidth={1}
              />
            )
          })}
        </Group>
        <Group top={TOP_MARGIN}>
          {data.map((d, i) => {
            const barX = xScale(d.name) ?? 0
            const barY = 0
            const nextBarX = xScale(data[i + 1]?.name) ?? 0
            let barWidth = nextBarX - barX
            if (i === data.length - 1) {
              barWidth = xScale.bandwidth()
            }

            const isHovered = hoveredBar === i
            if (isHovered) {
              return (
                <rect
                  key={`bar-${i}`}
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={yMax}
                  fill='url(#hovered-funnel-bar-gradient)'
                />
              )
            }

            return (
              <rect
                key={`bar-${i}`}
                x={barX}
                y={barY}
                width={barWidth - 1}
                height={yMax}
                fill='#fff'
                onMouseEnter={() => {
                  setHoveredBar(i)
                }}
                onMouseLeave={() => {
                  setHoveredBar(null)
                }}
              />
            )
          })}
        </Group>
        <Group top={TOP_MARGIN} className='fill-primary-dark'>
          {/* connecting lines */}
          {data.map((d, i) => {
            const barWidth = xScale.bandwidth()
            const barHeight = yScale(d.value) ?? 0
            const barX = xScale(d.name) ?? 0
            const barY = yMax - barHeight

            if (i < data.length - 1) {
              const nextD = data[i + 1]
              const nextBarX = xScale(nextD.name) ?? 0
              const nextBarHeight = yScale(nextD.value) ?? 0
              const nextBarY = yMax - nextBarHeight

              if (nextBarHeight === 0) {
                return null
              }

              return (
                <Group key={`group-${i}`} onMouseEnter={() => setHoveredBar(i)}>
                  <Polygon
                    points={[
                      [barX + barWidth - 2, barY + 2],
                      [nextBarX, nextBarY],
                      [nextBarX, yMax],
                      [barX + barWidth, yMax]
                    ]}
                    fill='#01CCA759'
                  />
                </Group>
              )
            }
            return null
          })}
          {/* bars */}
          {data.map((d, i) => {
            const barWidth = xScale.bandwidth()
            const barHeight = yScale(d.value) ?? 0
            const barX = xScale(d.name) ?? 0
            const barY = yMax - barHeight
            const isHovered = hoveredBar === i

            const nextBarX = xScale(data[i + 1]?.name) ?? 0
            let hoverLineWidth = nextBarX - barX
            if (i === data.length - 1) {
              hoverLineWidth = xScale.bandwidth()
            }

            const prevBarHeight = i > 0 ? yScale(data[i - 1].value) ?? 0 : barHeight

            return (
              <Group key={`bar-group-${i}`} onMouseEnter={() => setHoveredBar(i)} className='cursor-pointer'>
                {isHovered && i > 0 && (
                  <Line
                    from={{ x: barX, y: yMax - prevBarHeight }}
                    to={{ x: barX + hoverLineWidth, y: yMax - prevBarHeight }}
                    strokeWidth={1}
                    className='stroke-primary-dark'
                  />
                )}
                {barHeight > 0 && (
                  <path
                    key={`bar-${i}`}
                    d={`
                      M ${barX},${barY + barHeight}
                      L ${barX},${barY}
                      L ${barX + barWidth - 8},${barY}
                      Q ${barX + barWidth},${barY} ${barX + barWidth},${barY + 8}
                      L ${barX + barWidth},${barY + barHeight}
                      Z
                    `}
                    className='fill-primary-dark'
                  />
                )}
              </Group>
            )
          })}
        </Group>
      </svg>
      <div className='absolute top-0 left-0 w-full'>
        {data.map((d, i) => {
          const barX = xScale(d.name) ?? 0
          const isHovered = hoveredBar === i
          const numValue = parseFloat(d.comparedToPastPercentage)
          const trendText =
            numValue === 0 ? '' : <TrendIndicator value={numValue} formatter={value => `${Math.abs(value)}%`} />

          return (
            <div
              key={`text-group-${i}`}
              className={`absolute cursor-pointer transition-all duration-300 ${isHovered ? 'translate-y-2' : ''}`}
              style={{
                left: barX + 10,
                width: width / 6
              }}
              onMouseEnter={() => setHoveredBar(i)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              <div className='text-sm text-gray-900 mt-7 h-8'>{d.name}</div>
              <div>
                <div className='text-xl font-bold text-gray-900 mt-4'>{d.value}</div>
                <div className='flex flex-col items-start gap-2'>
                  <span className='text-sm text-gray-900'>{d.percentage}</span>
                  <span className='text-sm font-bold'>{trendText}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function FunnelChartBase({ data }: { data: FunnelChartData[] }) {
  return <ParentSize>{({ width, height }) => <Funnel width={width} height={height} data={data} />}</ParentSize>
}
